import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Handshake, ShieldCheck, Sparkles } from "lucide-react";
import { setMeta } from "../lib/seo";

const VALUES = [
  { icon: Compass, title: "Curation over clutter", text: "We track thousands of products across major merchants and surface only the ones with strong ratings, real discounts, and reliable fulfillment." },
  { icon: ShieldCheck, title: "Radical transparency", text: "We're an affiliate marketplace and proud of it. Every recommendation page tells you exactly how we earn — and that it never costs you extra." },
  { icon: Handshake, title: "Merchants we trust", text: "We feature established marketplaces and vetted affiliate partners, so your order, shipping, and returns are handled by companies with real infrastructure." },
  { icon: Sparkles, title: "Discovery made fun", text: "Trending lists, daily deals, and honest guides — shopping research that feels less like homework." },
];

export default function AboutPage() {
  useEffect(() => {
    setMeta(
      "About Us | ShoppinMagic",
      "ShoppinMagic is a curated affiliate marketplace helping shoppers discover trending products and honest deals from trusted merchants."
    );
  }, []);

  return (
    <div data-testid="about-page" className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <span className="text-xs font-bold uppercase tracking-wider text-gold-600">About ShoppinMagic</span>
      <h1 className="font-heading mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
        We put the magic back into online shopping.
      </h1>
      <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
        ShoppinMagic is a modern online marketplace that curates trending products from trusted merchants across the
        web. We started with a simple frustration: too many tabs, too many fake reviews, too much noise. So we built
        one place that does the filtering for you.
      </p>

      <div className="mt-8 rounded-2xl border border-gold-100 bg-gold-50 p-6" data-testid="about-model-note">
        <h2 className="font-heading text-lg font-bold text-slate-900">How we make money (the honest version)</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          We do <strong>not</strong> sell products directly. When you click a link on our site and buy from a merchant,
          we may earn an affiliate commission — at absolutely no extra cost to you. That commission model keeps
          ShoppinMagic free, independent, and focused on finding you genuinely good products.{" "}
          <Link to="/legal/affiliate-disclosure" className="font-semibold text-gold-600 underline">Read our full disclosure</Link>.
        </p>
      </div>

      <h2 className="font-heading mt-12 text-2xl font-bold text-slate-900 sm:text-3xl">What we stand for</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {VALUES.map(({ icon: Icon, title, text }) => (
          <div key={title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <Icon size={22} className="text-gold-600" />
            <h3 className="mt-3 font-bold text-slate-900">{title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{text}</p>
          </div>
        ))}
      </div>

      <h2 className="font-heading mt-12 text-2xl font-bold text-slate-900 sm:text-3xl">How affiliate links work here</h2>
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-600 sm:text-base">
        <li>You browse curated categories, deals, and guides on ShoppinMagic.</li>
        <li>You click "View on Merchant Site" — that link contains our affiliate tag.</li>
        <li>You buy (or don't) on the merchant's website, at their normal price.</li>
        <li>If you buy, the merchant pays us a small commission. You pay nothing extra. Ever.</li>
      </ol>

      <div className="mt-12 rounded-2xl bg-navy-900 px-6 py-10 text-center sm:px-12">
        <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">Ready to shop smarter?</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
          Explore trending products and today's best deals — all from merchants you already trust.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/trending" data-testid="about-cta-trending" className="inline-flex items-center gap-2 rounded-xl bg-gold-600 px-6 py-3 text-sm font-bold text-white hover:bg-gold-700">
            Explore trending <ArrowRight size={15} />
          </Link>
          <Link to="/deals" data-testid="about-cta-deals" className="inline-flex items-center gap-2 rounded-xl border border-gold-500 px-6 py-3 text-sm font-bold text-white hover:bg-navy-800">
            Today's deals
          </Link>
        </div>
      </div>
    </div>
  );
}
